import React, {useState} from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Stack } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import useAlert from '../../alert/useAlert';
import AlertDialog from '../../Dialog';

const MessageDisplay = ({ message, alignRight, senderLabel, onReply, textMessages, deleteTextMessage, updateTextMessage }) => {
  // alignRight === true => patient (shown on the right). AI (Virtual Assistant) and therapist go left.
  const userCheck = alignRight;
  const alignSelf = userCheck ? 'flex-end' : 'flex-start';
  const backgroundColor = message.ispinned ? '#ffea99' : userCheck ? '#B9F4F2' : '#ECEEF0';
  const textColor = 'black';
  const [hovered, setHovered] = useState(false);
  const { setAlert } = useAlert();
  const [openDialog, setOpenDialog] = useState({ open: false, content: '', confirmAction: null });

  const borderRadius = userCheck
  ? '15px 15px 0px 15px'
  : '15px 15px 15px 0px';

  // The quoted message may have been soft-deleted or not be in the loaded set — guard the lookup.
  const repliedMessageText = message.replymessageid
    ? (textMessages.find((msg) => msg.id === message.replymessageid)?.message ?? '')
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.message);
    setAlert('Message copied to clipboard!', 'success');
  };

  const handleDelete = async () => {
    await deleteTextMessage(message.id);
  }

  const handlePin = async () => {
    await updateTextMessage(message.id, !message.ispinned);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: alignSelf, mb: 2 }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {message.replymessageid && (
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            padding: '20px 5px',
            borderRadius: '15px',
            maxWidth: '60%',
            display: 'fle ',
            direction: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            mr: userCheck ? '2vw' : 0,
            ml: userCheck ? 0 : '2vw',
            zIndex: 0,
            mb: -2
          }}
        >
        <Typography variant="body2" color="text.secondary" sx={{ padding: '0px 10px' }}>
          <div
            dangerouslySetInnerHTML={{
              __html: repliedMessageText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br />')
            }}
          />
        </Typography>
      </Box>
      )}
      <Stack direction="row" alignItems='flex-start' spacing={2} sx={{mr: userCheck ? '2vw' : 0, ml: userCheck ? 0 : '2vw', width: '100%', flexDirection: userCheck ? 'row-reverse' : 'row',}}>
        <Paper
          elevation={3}
          sx={{
            maxWidth: '60%',
            padding: '10px',
            backgroundColor: backgroundColor,
            color: textColor,
            borderRadius: borderRadius,
            wordBreak: 'break-word',
            zIndex: 1
          }}
        >
          <Typography>
            <div
              dangerouslySetInnerHTML={{
                __html: message.message.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br />')
              }}
            />
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#000', width: '100%', marginTop: '5px', }}>
            {`${message.created_at.split('T')[0]} ${message.created_at.split('T')[1].split(':').slice(0, 2).join(':')}`}
          </Typography>
        </Paper>
        {hovered && (
            <Box sx={{ display: 'flex', gap: 1, flexDirection: userCheck ? 'row-reverse' : 'row',}}>
              <Tooltip title="Copy message">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reply to message">
                <IconButton size="small" onClick={() => onReply(message, senderLabel)}>
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete message">
                <IconButton size="small" onClick={(e) => { e.preventDefault(); setOpenDialog({open: true, content: "Are you sure you want to delete this message?", confirmAction: () => handleDelete()}); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title= {message.ispinned ? "Unpin message" : "Pin message"}>
                <IconButton size="small" onClick={(e) => { handlePin() }}>
                  <PushPinIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
      </Stack>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: userCheck ? 'flex-end' : 'flex-start', justifyContent: 'flex-start' }}>
        <IconButton
        size="large"
        edge="end"
        onClick={() => {}}
        >
            <AccountCircle sx={{ fontSize: '30px', marginRight: '10px' }}/>
        </IconButton>
        <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>{ senderLabel }</Typography>
      </Box>
      {
        openDialog && (
          <AlertDialog data={{open: openDialog.open, content: openDialog.content, confirmAction: openDialog.confirmAction, setOpen: setOpenDialog}}/>
        )
      }
    </Box>
  );
};

export default MessageDisplay;
